'use client';
import React from 'react';
import { BehaviorLog } from '@/lib/services/forgeBehaviorService';

interface SleepTrainingCorrelationProps {
  logs: BehaviorLog[];
}

function avg(arr: number[]): number {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

export default function SleepTrainingCorrelation({ logs }: SleepTrainingCorrelationProps) {
  const cannabisDays = logs.filter(l => l.cannabisAmount !== 'none');
  const nonCannabisDays = logs.filter(l => l.cannabisAmount === 'none');

  const cannabisSleep = cannabisDays.filter(l => l.sleepDurationHours != null);
  const nonCannabisSleep = nonCannabisDays.filter(l => l.sleepDurationHours != null);
  const cannabisQuality = cannabisDays.filter(l => l.sleepQualityRating != null);
  const nonCannabisQuality = nonCannabisDays.filter(l => l.sleepQualityRating != null);
  const cannabisEnergy = cannabisDays.filter(l => l.morningEnergyRating != null);
  const nonCannabisEnergy = nonCannabisDays.filter(l => l.morningEnergyRating != null);

  const cannabisTraining = cannabisDays.filter(l => l.trainingCompleted).length;
  const nonCannabisTraining = nonCannabisDays.filter(l => l.trainingCompleted).length;
  const cannabisTrainingPct = cannabisDays.length > 0 ? Math.round((cannabisTraining / cannabisDays.length) * 100) : 0;
  const nonCannabisTrainingPct = nonCannabisDays.length > 0 ? Math.round((nonCannabisTraining / nonCannabisDays.length) * 100) : 0;

  const hasData = logs.length >= 4;

  const sleepMetrics = [
    {
      label: 'Avg Sleep Duration',
      cannabis: cannabisSleep.length > 0 ? avg(cannabisSleep.map(l => l.sleepDurationHours!)).toFixed(1) + 'h' : '—',
      nonCannabis: nonCannabisSleep.length > 0 ? avg(nonCannabisSleep.map(l => l.sleepDurationHours!)).toFixed(1) + 'h' : '—',
      diff: cannabisSleep.length > 0 && nonCannabisSleep.length > 0
        ? avg(cannabisSleep.map(l => l.sleepDurationHours!)) - avg(nonCannabisSleep.map(l => l.sleepDurationHours!))
        : null,
    },
    {
      label: 'Sleep Quality (1–10)',
      cannabis: cannabisQuality.length > 0 ? avg(cannabisQuality.map(l => l.sleepQualityRating!)).toFixed(1) : '—',
      nonCannabis: nonCannabisQuality.length > 0 ? avg(nonCannabisQuality.map(l => l.sleepQualityRating!)).toFixed(1) : '—',
      diff: cannabisQuality.length > 0 && nonCannabisQuality.length > 0
        ? avg(cannabisQuality.map(l => l.sleepQualityRating!)) - avg(nonCannabisQuality.map(l => l.sleepQualityRating!))
        : null,
    },
    {
      label: 'Morning Energy (1–10)',
      cannabis: cannabisEnergy.length > 0 ? avg(cannabisEnergy.map(l => l.morningEnergyRating!)).toFixed(1) : '—',
      nonCannabis: nonCannabisEnergy.length > 0 ? avg(nonCannabisEnergy.map(l => l.morningEnergyRating!)).toFixed(1) : '—',
      diff: cannabisEnergy.length > 0 && nonCannabisEnergy.length > 0
        ? avg(cannabisEnergy.map(l => l.morningEnergyRating!)) - avg(nonCannabisEnergy.map(l => l.morningEnergyRating!))
        : null,
    },
  ];

  const trainingDiff = cannabisTrainingPct - nonCannabisTrainingPct;
  const trainingConclusion = Math.abs(trainingDiff) < 5
    ? 'No meaningful difference in training frequency.'
    : trainingDiff > 0
    ? 'Cannabis days appear associated with more training.' :'Cannabis days appear associated with less training.';

  return (
    <div className="card-forge p-5 space-y-5">
      <div>
        <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">Sleep & Training Correlation</h3>
        <p className="text-2xs text-muted-foreground mt-0.5">Cannabis days vs non-cannabis days</p>
      </div>

      {!hasData ? (
        <div className="py-6 text-center">
          <p className="text-muted-foreground text-sm">Log at least 4 days with sleep data to see correlations.</p>
        </div>
      ) : (
        <>
          {/* Sleep comparison */}
          <div className="space-y-1">
            <div className="grid grid-cols-3 gap-2 text-2xs text-muted-foreground font-bold uppercase tracking-wider mb-2">
              <span>Metric</span>
              <span className="text-center text-primary/70">Cannabis Days</span>
              <span className="text-center">Non-Cannabis</span>
            </div>
            {sleepMetrics.map(m => (
              <div key={m.label} className="grid grid-cols-3 gap-2 items-center py-2 border-b border-border/50">
                <span className="text-xs text-muted-foreground">{m.label}</span>
                <span className="text-center text-sm font-bold text-foreground">{m.cannabis}</span>
                <div className="flex items-center justify-center gap-1.5">
                  <span className="text-sm font-bold text-foreground">{m.nonCannabis}</span>
                  {m.diff != null && (
                    <span className={`text-2xs font-bold ${Math.abs(m.diff) < 0.2 ? 'text-muted-foreground' : m.diff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {m.diff > 0 ? '+' : ''}{m.diff.toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Training comparison */}
          <div className="bg-card-elevated rounded-lg p-4 space-y-3">
            <div className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Training Frequency</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xs text-primary/70 mb-1">Cannabis Days</div>
                <div className="text-2xl font-black text-foreground">{cannabisTrainingPct}%</div>
                <div className="text-2xs text-muted-foreground">trained</div>
              </div>
              <div className="text-center">
                <div className="text-2xs text-muted-foreground mb-1">Non-Cannabis Days</div>
                <div className="text-2xl font-black text-foreground">{nonCannabisTrainingPct}%</div>
                <div className="text-2xs text-muted-foreground">trained</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground italic">{trainingConclusion}</p>
          </div>

          <p className="text-2xs text-muted-foreground/50 italic">
            Correlation does not necessarily equal causation. These are observations only.
          </p>
        </>
      )}
    </div>
  );
}
